import "./TrackEditor.scss";
import React, { useMemo, useState } from "react";
import { Dialog } from "App/common/Dialog";
import { ErrorBoundary } from "App/common/ErrorBoundary";
import { TabbedView } from "App/core/TabbedView";
import { TrackDetails } from "./TrackDetails";
import { useTrack } from "../useEntities/useTrack";
import { TrackAttachments } from "./TrackAttachments";
import { AttachmentsContextProvider } from "App/common/useAttachments";
import { Space } from "@highnote/server/src/core/entities";
import { ViewingSpaceId } from "App/modules/viewingSpace";

export enum TRACK_EDITOR_TAB {
  DETAILS = "details",
  ATTACHMENTS = "attachments",
}

export const TrackEditor = ({
  space,
  onClose,
  initialTab = TRACK_EDITOR_TAB.DETAILS,
  viewingSpaceId,
}: {
  space?: Space;
  onClose?: () => void;
  initialTab?: TRACK_EDITOR_TAB;
  /**
   * The space this editor was opened FROM, supplied by the opener (see
   * `useTrackEditor`). Not read from the route: the editor also opens for
   * tracks from OTHER spaces (the move/add dialogs' lists), where the
   * route's space would be a space the track is not in and the server
   * would deny what it allows with no viewing space at all.
   */
  viewingSpaceId?: ViewingSpaceId;
}) => {
  const { track } = useTrack();
  const [activeTabId, setActiveTabId] = useState<TRACK_EDITOR_TAB>(initialTab);

  const trackTabs = useMemo(() => {
    return [
      {
        id: TRACK_EDITOR_TAB.DETAILS,
        name: "Details",
        view: <TrackDetails space={space} viewingSpaceId={viewingSpaceId} />,
        onClick: () => setActiveTabId(TRACK_EDITOR_TAB.DETAILS),
        keepOnDismount: true,
      },
      {
        id: TRACK_EDITOR_TAB.ATTACHMENTS,
        name: "Attachments",
        view: <TrackAttachments space={space} />,
        onClick: () => setActiveTabId(TRACK_EDITOR_TAB.ATTACHMENTS),
      },
    ];
  }, [space, viewingSpaceId]);

  if (!track) {
    return null;
  }

  return (
    <ErrorBoundary name="TrackEditor">
      <Dialog
        title={"Track Details"}
        open
        onClose={onClose}
        className="highnote-track-editor"
      >
        {/*
          `track.files` are TRACK attachments, so the server authorizes them
          through canViewTrackFromSpace — which, given no viewing space,
          falls back to `track.spaceId` (the ORIGIN). A member of a
          borrowing space fails that check, GET_FILES filters the ids out of
          its 200 rather than throwing, and the Attachments tab renders
          empty with nothing in the console.

          The two sibling providers (SpaceHomeV2, ExpandableSpaceRow) pass
          SPACE attachments, which the server's SPACE_ATTACHMENT arm
          authorizes on its own and which ignore this prop.
        */}
        <AttachmentsContextProvider
          space={space}
          spaceId={viewingSpaceId}
          attachmentIds={track.files || []}
          track={track}
        >
          <TabbedView tabs={trackTabs} activeTabId={activeTabId} />
        </AttachmentsContextProvider>
      </Dialog>
    </ErrorBoundary>
  );
};
